import { UserAccessInfoDTO, UserAccessInviteDTO } from 'api-models'
import {
	DotMatrix,
	Avatar,
	Badge,
	DataGrid,
	PageHeader,
	StatCard
} from '@clickguard/design-system'
import type { DataGridColumn } from '@clickguard/design-system'
import { useEffect, useState } from 'react'
import { RiUserAddLine } from 'react-icons/ri'
import { sanitizeURL } from 'utils'
import { PageTitle } from '../../../utils/page-title'
import { AddMember } from './add-member'
import { ManagerMember } from './manage-memeber'
import { RevokeInvite } from './revoke-invite'
import { useAccountStore } from 'store'
import { useShallow } from 'zustand/react/shallow'

type MemberRow =
	| { kind: 'access'; access: UserAccessInfoDTO }
	| { kind: 'invite'; invite: UserAccessInviteDTO }

const roleLabel = (level?: string) =>
	level === 'READ'
		? 'Analyst'
		: level === 'WRITE'
		? 'Manager'
		: level === 'OWNER'
		? 'Owner'
		: 'Other'

// The installed DS Badge is an older spec than Figma's Badge (no border,
// full-pill radius, 10px font), so we override every property to the exact
// Figma values per role (node 591:46246 etc.: 16px radius, 1px border,
// 12/16 SemiBold, px-8).
const BADGE_BASE =
	'!rounded-[16px] !border !px-2 !text-[12px] !leading-[16px] !font-semibold'

const ROLE_BADGE: Record<string, string> = {
	OWNER: '!bg-t-step-inactive !border-[#554bfd] !text-[color:var(--brand-chip-ink,#3420a4)]',
	WRITE: '!bg-bg-plan-fill !border-t-checkbox !text-[color:var(--text-secondary)]',
	READ: '!bg-[#ecfdf3] !border-[#a6f4c5] !text-[#067647]'
}

const INVITED_BADGE = '!bg-[#fffaeb] !border-[#fedf89] !text-[#b54708]'

export const TeamAccessPage = () => {
	const [loading, setLoading] = useState(true)
	const {
		account,
		getAccountUsers,
		accountInvitedUsers,
		accountUsersWithAccess,
		wantsToSeeDemoData
	} = useAccountStore(
		useShallow((state) => ({
			account: state.account,
			getAccountUsers: state.getAccountUsers,
			accountInvitedUsers: state.accountInvitedUsers,
			accountUsersWithAccess: state.accountUsersWithAccess,
			wantsToSeeDemoData: state.wantsToSeeDemoData
		}))
	)

	useEffect(() => {
		async function init() {
			if (account) {
				await getAccountUsers(account.id)
				setLoading(false)
			}
		}
		init()
	}, [account])

	const rows: MemberRow[] = [
		...accountUsersWithAccess.map(
			(access): MemberRow => ({ kind: 'access', access })
		),
		...accountInvitedUsers.map(
			(invite): MemberRow => ({ kind: 'invite', invite })
		)
	]

	const columns: DataGridColumn<MemberRow>[] = [
		{
			id: 'name',
			header: 'Name',
			cell: (r) => {
				if (r.kind === 'access') {
					const name = r.access.user_name || r.access.user_email
					return (
						<div className="flex min-w-0 items-center gap-3">
							<Avatar
								size="xs"
								src={r.access.avatar || undefined}
								name={name || undefined}
							/>
							<div className="flex min-w-0 flex-col">
								<span className="truncate text-[14px] leading-[18px] text-[color:var(--text-primary)]">
									{name}
								</span>
								{r.access.user_name && (
									<span className="truncate text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
										{r.access.user_email}
									</span>
								)}
							</div>
						</div>
					)
				}
				return (
					<div className="flex min-w-0 items-center gap-3">
						<Avatar size="xs" name={r.invite.user_email || undefined} />
						<span className="truncate text-[14px] leading-[18px] text-[color:var(--text-primary)]">
							{r.invite.user_email}
						</span>
					</div>
				)
			}
		},
		{
			id: 'role',
			header: 'Role',
			width: 160,
			cell: (r) => {
				const level =
					r.kind === 'access' ? r.access.access_level : r.invite.access_level
				return (
					<div className="flex items-center gap-2">
						<Badge
							variant="gray"
							size="sm"
							className={`${BADGE_BASE} ${
								ROLE_BADGE[level ?? ''] ?? ROLE_BADGE.WRITE
							}`}
						>
							{roleLabel(level)}
						</Badge>
						{r.kind === 'invite' && r.invite.code && (
							<Badge
								variant="gray"
								size="sm"
								className={`${BADGE_BASE} ${INVITED_BADGE}`}
							>
								Invited
							</Badge>
						)}
					</div>
				)
			}
		},
		{
			id: 'options',
			header: 'Options',
			width: 131,
			align: 'right',
			cell: (r) => {
				if (r.kind === 'access') {
					return r.access.access_level !== 'OWNER' ? (
						<ManagerMember row={r.access} />
					) : null
				}
				return <RevokeInvite row={r.invite} />
			}
		}
	]

	if (!wantsToSeeDemoData && loading) {
		return (
			<div className="flex justify-center py-16">
				<DotMatrix size="lg" />
			</div>
		)
	}

	return (
		<div className="w-full">
			<PageTitle pageName="Team access" />
			<PageHeader
				layout="stacked"
				title="Team access"
				subtitle={
					<span className="inline-flex items-center">
						Manage team members and their permissions for&nbsp;
						<span className="!font-semibold max-w-[200px] truncate">
							{sanitizeURL(account?.name) || 'Demo'} Workspace
						</span>
					</span>
				}
			/>
			<div className="pt-4">
				<StatCard
					size="regular"
					divider={false}
					className="w-full !rounded-xl !border-t-traffic !p-4 !gap-4 !shadow-[0px_4px_8px_0px_rgba(16,24,40,0.03),0px_1px_2px_0px_rgba(16,24,40,0.02)]"
					title={
						<span className="text-[18px] leading-6 font-medium text-t-base-blue">
							Team members
						</span>
					}
					trailing={<AddMember />}
				>
					<DataGrid<MemberRow>
						data={rows}
						getRowId={(_row, index) => String(index)}
						columns={columns}
						emptyState={
							<div className="mt-6 flex flex-col items-center text-center">
								<RiUserAddLine className="h-10 w-10 text-[#9f9ea3]" />
								<h3 className="mt-2 text-[14px] font-medium text-[color:var(--text-primary)]">
									No team members
								</h3>
								<p className="mt-1 text-[14px] text-[color:var(--text-secondary)]">
									You are the only user with access to this workspace
								</p>
							</div>
						}
					/>
				</StatCard>
			</div>
		</div>
	)
}
